/**
 * ServicePageData for the "Data Quality & Enhancement" page (route: /data-quality-enhancement/).
 *
 * Content and layout are sourced directly from 02-data-quality-enhancement.html — every
 * heading, paragraph, and figure below is that file's wording, unchanged. Colours use the
 * site's existing pink (#db0058) / charcoal (#232528) palette on the shared components'
 * new section variants (ServiceBenefits' "pillars"/"outcomes", ServiceContentSection's
 * `highlight` and feature-variant `icon`, and ServiceProcessSteps) — the reference file's
 * own navy/teal was not carried over, matching how customerAcquisition.jsx treats its
 * own reference mockup.
 *
 * @type {import('../../components/service/ServicePageLayout').ServicePageData}
 */
const dataQualityEnhancementPageData = {
  slug: "data-quality-enhancement",
  title: "Data Quality & Enhancement",
  introduction:
    "Accurate, complete & thorough. Our data-enhancing techniques enable better decision-making through actionable insights.",
  hero: {
    eyebrow: "Data Services",
    align: "left",
    // Links to the enquiry form at the bottom of this same page — see enquiryForm.anchorId below.
    cta: { label: "Enquire now", href: "#enquiry" },
  },

  sections: [
    {
      type: "benefits",
      variant: "pillars",
      items: [
        {
          icon: "check",
          title: "Clean, Detailed & Reliable",
          description:
            "Our data quality services improve your marketing data to ensure detailed, decision-ready information — fast & efficient.",
        },
        {
          icon: "insights",
          title: "Unique Actionable Insights",
          description:
            "We enhance and drive actionable insight from your customer data, leveraging proven modelling and analytics capability.",
        },
        {
          icon: "shield",
          title: "Regulation & Compliance",
          description:
            "Navigate GDPR with confidence. Our comprehensive suppression techniques keep your data on-point and fully compliant.",
        },
      ],
    },
    {
      id: "data-suppression",
      title: "Data Suppression",
      titleDivider: true,
      content: [
        "Improve data accuracy and reduce waste on marketing spend — remove inaccurate data to improve compliance, efficiency, and ROI.",
      ],
      highlight: {
        heading: "Remove wasteful, inaccurate customer data",
        content: [
          "Our leading data suppression files ensure your customer and prospect data is up to date and compliant, enabling you to deploy high-performing marketing campaigns with increased engagement propensity and minimal contact waste. Suppressing your data saves you time, money and resources by removing individuals who won't or don't want to receive your marketing messaging.",
        ],
      },
    },
    {
      type: "process",
      items: [
        {
          number: "01",
          label: "Receive Your Data",
          description:
            "We ingest your existing customer & prospect records securely",
        },
        {
          number: "02",
          label: "Analyse & Match",
          description: "Cross-reference against suppression files & RESIDENT",
        },
        {
          number: "03",
          label: "Suppress & Enhance",
          description: "Remove invalid records, enrich remaining data",
        },
        {
          number: "04",
          label: "Deliver Insights",
          description:
            "Return clean, enriched & compliant dataset ready to use",
        },
      ],
    },
    {
      type: "benefits",
      variant: "outcomes",
      items: [
        {
          title: "Improved Compliance",
          description:
            "Meets GDPR Article 5 requirements — keeping data accurate, up-to-date, and legally defensible at all times.",
        },
        {
          title: "Reduced Marketing Waste",
          description:
            "Eliminate spend on contacts who can't or won't respond, maximising your campaign budget efficiency.",
        },
        {
          title: "Higher Engagement Rates",
          description:
            "Cleaner data means messages reach real, reachable people — improving open rates, conversions and ROI.",
        },
        {
          title: "Faster Decision-Making",
          description:
            "Reliable, enriched data enables your team to act with confidence and move campaigns forward quickly.",
        },
      ],
    },
    {
      id: "gdpr-compliant-by-design",
      variant: "feature",
      icon: "shield",
      title: "GDPR Compliant by Design",
      content: [
        "All data suppression and enhancement services are built around regulatory best practice. We help you navigate GDPR Article 5 requirements with complete confidence — protecting your business and your customers.",
      ],
    },
  ],

  pricing: null,

  enquiryForm: {
    enabled: true,
    serviceName: "Data Quality and Enhancement",
    anchorId: "enquiry",
    heading: "Enquire now",
  },

  seo: {
    title: "Data Quality & Enhancement - Lloyd James Data Consultancy",
    description:
      "Improve marketing data accuracy with our data quality and enhancement services — GDPR Article 5 compliant suppression, enrichment and cleansing for better ROI.",
    keywords:
      "data quality, data enhancement, data suppression, marketing data accuracy, GDPR compliant marketing data, data cleansing, Lloyd James",
    canonical: "/data-quality-enhancement/",
  },
};

export default dataQualityEnhancementPageData;
